import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, ArrowRight, ArrowLeft, Sparkles, ShoppingBag, Star } from 'lucide-react';
import axios from 'axios';
import LazyImage from '../components/LazyImage';
import { playReveal, playClickSoft } from '../utils/sound';
import { trackQuizComplete } from '../utils/analytics';
import SEOHead from '../components/SEOHead';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const API = `${window.location.origin}/api`;

const STEPS = [
  {
    title: "What's the vibe?",
    subtitle: "Saan mo gagamitin?",
    field: "mood",
    options: [
      { value: "Office", label: "Daily / Office", desc: "Pang-araw-araw, fresh at clean", icon: "briefcase" },
      { value: "Date Night", label: "Date Night", desc: "Seductive at long-lasting", icon: "heart" },
      { value: "Gym", label: "Gym / Active", desc: "Light at refreshing", icon: "dumbbell" },
      { value: "Luxury", label: "Luxury / Signature", desc: "Stand out, premium aura", icon: "crown" },
    ],
  },
  {
    title: "Anong type ng scent?",
    subtitle: "Pick your preference",
    field: "preference",
    options: [
      { value: "Fresh", label: "Fresh / Citrus", desc: "Malinis, parang bagong ligo", icon: "droplets" },
      { value: "Sweet", label: "Sweet / Gourmand", desc: "Matamis, vanilla vibes", icon: "candy" },
      { value: "Woody", label: "Woody / Masculine", desc: "Mabango, earthy at warm", icon: "tree" },
      { value: "Floral", label: "Floral / Feminine", desc: "Bulaklak, elegant at soft", icon: "flower" },
    ],
  },
  {
    title: "Gaano ka-strong?",
    subtitle: "How strong do you want it?",
    field: "strength",
    options: [
      { value: "Light", label: "Light", desc: "Subtle, close-contact lang", icon: "wind" },
      { value: "Moderate", label: "Moderate", desc: "Noticeable, hindi overwhelming", icon: "zap" },
      { value: "Strong", label: "Beast Mode", desc: "Malakas, may sillage trail", icon: "flame" },
    ],
  },
];

export default function ScentFinderPage() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ mood: '', preference: '', strength: '', gender: '' });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const selectOption = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
    if (step < STEPS.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    }
  };

  const findScent = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/scent-finder`, answers);
      setResults(res.data);
      trackQuizComplete(res.data?.style || answers?.style);
      setTimeout(() => playReveal(), 200);
    } catch (e) {
      console.error('Scent finder error:', e);
      setResults({ recommendations: [], message: "Something went wrong. Please try again." });
    }
    setLoading(false);
  };

  // Gender selection (shown before step 0)
  if (!answers.gender) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-8 pb-20">
        <SEOHead
          title="Scent Finder Quiz"
          description="Take our 5-question quiz to discover your signature fragrance. Get personalized EDP (30% oil, Extrait-level) recommendations from JS Fragrance based on your style and preferences."
          url="https://jsfragrances.com/scent-finder"
        />
        <BreadcrumbSchema crumbs={[{ name: 'Home', path: '/' }, { name: 'Scent Finder', path: '/scent-finder' }]} />
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-12">
            <Compass className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-light text-white mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Scent Finder
            </h1>
            <p className="text-white/50 text-sm font-sans">Answer 3 quick questions. We'll find your perfect scent.</p>
          </div>

          <p className="text-center text-white/70 text-sm uppercase tracking-[0.2em] font-sans mb-6">Shopping for...</p>
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[
              { value: 'Men', label: 'Him' },
              { value: 'Women', label: 'Her' },
              { value: 'All', label: 'Anyone' },
            ].map(g => (
              <button
                key={g.value}
                onClick={() => setAnswers(prev => ({ ...prev, gender: g.value }))}
                className="py-6 border border-white/10 hover:border-[#D4AF37]/50 text-white/70 hover:text-[#D4AF37] transition-all text-sm uppercase tracking-[0.2em] font-sans"
                data-testid={`gender-${g.value.toLowerCase()}`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Results page
  if (results) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-8 pb-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Your Perfect Match
            </h1>
            <p className="text-white/50 text-sm font-sans">{results.message}</p>
          </div>

          <div className="space-y-4">
            {results.recommendations?.map((product, idx) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="flex flex-col sm:flex-row gap-4 p-4 bg-[#141414] border border-white/5 hover:border-[#D4AF37]/30 transition-all group"
                data-testid={`recommendation-${idx}`}
              >
                <div className="w-full sm:w-32 h-40 sm:h-32 flex-shrink-0 overflow-hidden">
                  <LazyImage src={product.image_url} alt={product.name} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {product.match_score && (
                      <span className="text-[10px] px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] font-sans uppercase tracking-wider">
                        {product.match_score}% Match
                      </span>
                    )}
                    <span className="text-white/30 text-xs font-sans">{product.gender}</span>
                  </div>
                  <h3 className="text-lg text-white group-hover:text-[#D4AF37] transition-colors mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {product.name}
                  </h3>
                  {product.why_it_fits && (
                    <p className="text-white/50 text-sm font-sans leading-relaxed mb-2">{product.why_it_fits}</p>
                  )}
                  <p className="text-[#D4AF37] text-sm font-sans font-medium">₱{product.base_price?.toLocaleString()}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => { setResults(null); setStep(0); setAnswers({ mood: '', preference: '', strength: '', gender: '' }); }}
              className="px-6 py-3 border border-white/10 text-white/70 hover:text-white text-sm uppercase tracking-[0.15em] font-sans transition-colors"
              data-testid="retake-quiz-button"
            >
              <ArrowLeft className="w-4 h-4 inline mr-2" />
              Retake Quiz
            </button>
            <Link
              to="/shop"
              className="px-6 py-3 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#C4A030] transition-colors"
              data-testid="browse-all-button"
            >
              Browse All Scents
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Quiz steps
  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;
  const canSubmit = answers.mood && answers.preference && answers.strength;

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-8 pb-20">
      <div className="max-w-2xl mx-auto px-4">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-0.5 flex-1 transition-colors duration-300 ${
                i <= step ? 'bg-[#D4AF37]' : 'bg-white/10'
              }`}
            />
          ))}
        </div>

        {/* Step header */}
        <div className="text-center mb-8">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-sans mb-2">Step {step + 1} of {STEPS.length}</p>
          <h2 className="text-3xl sm:text-4xl font-light text-white mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {currentStep.title}
          </h2>
          <p className="text-white/40 text-sm font-sans">{currentStep.subtitle}</p>
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {currentStep.options.map(opt => {
            const isSelected = answers[currentStep.field] === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => selectOption(currentStep.field, opt.value)}
                className={`text-left p-5 border transition-all duration-200 ${
                  isSelected
                    ? 'border-[#D4AF37] bg-[#D4AF37]/5'
                    : 'border-white/10 hover:border-white/20 bg-[#141414]'
                }`}
                data-testid={`option-${opt.value.toLowerCase().replace(/\s/g, '-')}`}
              >
                <p className={`text-sm font-sans font-medium mb-0.5 ${isSelected ? 'text-[#D4AF37]' : 'text-white'}`}>
                  {opt.label}
                </p>
                <p className="text-white/40 text-xs font-sans">{opt.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={() => step > 0 ? setStep(step - 1) : setAnswers(prev => ({ ...prev, gender: '' }))}
            className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-sans transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {isLastStep && canSubmit && (
            <button
              onClick={findScent}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black text-sm uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#C4A030] transition-colors disabled:opacity-50"
              data-testid="find-scent-button"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Finding...
                </span>
              ) : (
                <>Find My Scent <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
